import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { FiBookOpen, FiSliders } from "react-icons/fi";

import { isAnonymousUser, selectUser } from "redux/authReducer";
import {
  setShowBugReportModal,
  setShowLearnMoreModal,
  setshowProfileModal,
  submitBugReport,
} from "redux/Modals/actions";

import {
  AMP_DATASET_EXPLORATION_EVENT__LEARN_MORE_MODAL__OPENED,
  AMP_DOCUMENTATION_VISITED,
} from "helpers/constants/amplitudeEvents";
import {
  PRIVATE_DATASETS_PATH,
  PUBLIC_DATASETS_PATH,
} from "helpers/constants/paths";
import { amplitudeTrack } from "helpers/utility/amplitude";

import InvitationModal from "views/modals/InvitationModal";
import ProfileModal from "views/modals/ProfileModal";
import VLDatasetIcon from "views/uikit/VLDatasetIcon";
import VLAvatar from "views/uikit/VLAvatar";
import VLIcon from "views/uikit/VLIcon";

import BugReporter from "assets/icons/BugReporter";
import LocalIcon from "assets/icons/LocalIcon";

import styles from "./style.module.scss";
import { isFastdupUser, isOnPremUser } from "helpers/utility/utilities";
import BugReporterModal from "views/modals/BugReporterModal";
import TaskManagerIcon from "assets/icons/taskManagerIcon";
import ModelCatalogIcon from "assets/icons/ModelCatalogIcon";
import { useFeatureFlagService } from "hooks/useFeatureFlagService";
import {
  MODELS_CATALOG,
  RUNTIME_CONFIG,
  USER_MANAGEMENT,
} from "helpers/constants/filters";
import { Typography } from "antd";
import classNames from "classnames";
import { setSeverityData } from "redux/SingleDataset/actions";
import SettingsIcon from "assets/icons/SettingsIcon";
import VLTooltip from "views/uikit/VLTooltip";

const Sidebar = ({
  userAvatar,
  showBugReporter,
}: {
  userAvatar: string | undefined;
  showBugReporter: boolean;
}) => {
  const pathName = useLocation().pathname;
  const user = selectUser();
  const userInfo = useSelector((state: any) => state.user.userInfo);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const vlVersion = localStorage.getItem("vlVersion") || null;
  const isAllowedUser = (user && !isAnonymousUser()) || isOnPremUser();

  const handleAvatarClick = () => {
    if (isAllowedUser) {
      dispatch(setshowProfileModal(true));
    }
  };

  const handleLogoClick = () => {
    dispatch(setSeverityData(null));
    if (isAllowedUser) {
      navigate(PRIVATE_DATASETS_PATH);
    } else {
      navigate(PUBLIC_DATASETS_PATH);
    }
  };

  const handleDocsButtonClick = (e: any) => {
    e.stopPropagation();
    amplitudeTrack(AMP_DOCUMENTATION_VISITED);
  };

  const handleLearnMoreButtonClick = () => {
    dispatch(setShowLearnMoreModal(true));
    amplitudeTrack(AMP_DATASET_EXPLORATION_EVENT__LEARN_MORE_MODAL__OPENED);
  };

  const handleBugReporterClick = () => {
    dispatch(setShowBugReportModal(true));
  };

  const avatarClass = isAllowedUser ? styles.avatarContainer : styles.noUser;
  const showBugReporterIcon = userInfo?.isInternalUser;
  const isModelsCatalogEnabled = useFeatureFlagService(MODELS_CATALOG).enabled;
  const isUserManagementEnabled =
    useFeatureFlagService(USER_MANAGEMENT).enabled;
  const isRuntimeConfigEnabled = useFeatureFlagService(RUNTIME_CONFIG).enabled;
  const { Link } = Typography;

  const isOnTaskManagerPage = pathName === "/task-manager";
  const isOnModelsCatalogPage = pathName === "/models-catalog";
  const isWorkspaceSettingsPage = pathName === "/workspace-settings";
  const isRuntimeConfigPage = pathName === "/runtime-config";

  const isOnDatasetsPage =
    pathName === PRIVATE_DATASETS_PATH ||
    pathName === PUBLIC_DATASETS_PATH ||
    pathName.startsWith("/dataset/");

  return (
    <div className={classNames(styles.sidebar)}>
      <div className={styles.sidebarContainer}>
        <div className={styles.sidebarTop}>
          <span className={styles.vlLogo} onClick={handleLogoClick}>
            <VLIcon />
          </span>
          <div
            className={classNames(styles.menuItem, {
              [styles.active]: isOnDatasetsPage,
            })}
          >
            <VLDatasetIcon
              classNames={`${styles.datasetIcon} ${isOnDatasetsPage ? styles.active : ""}`}
            />
          </div>
          <div
            className={classNames(styles.menuItem, {
              [styles.active]: isOnTaskManagerPage,
            })}
          >
            <VLTooltip title={"Task manager"} placement="right">
              <Link
                className={`${styles.taskManagerIcon} ${isOnTaskManagerPage ? styles.active : ""}`}
                onClick={() => navigate("/task-manager")}
              >
                <TaskManagerIcon />
              </Link>
            </VLTooltip>
          </div>
          {isModelsCatalogEnabled && (
            <div
              className={classNames(styles.menuItem, {
                [styles.active]: isOnModelsCatalogPage,
              })}
            >
              <VLTooltip title={"Models Catalog"} placement="right">
                <Link
                  className={`${styles.modelsCatalogIcon} ${isOnModelsCatalogPage ? styles.active : ""}`}
                  onClick={() => navigate("/models-catalog")}
                >
                  <ModelCatalogIcon />
                </Link>
              </VLTooltip>
            </div>
          )}
        </div>

        <div className={styles.sidebarBottom}>
          {isRuntimeConfigEnabled && (
            <div
              className={classNames(styles.menuItem, {
                [styles.active]: isRuntimeConfigPage,
              })}
            >
              <VLTooltip title={"Runtime Config"} placement="right">
                <Link
                  className={`${styles.runtimeConfigIcon} ${isRuntimeConfigPage ? styles.active : ""}`}
                  onClick={() => navigate("/runtime-config")}
                >
                  <FiSliders size="24" />
                </Link>
              </VLTooltip>
            </div>
          )}
          {isOnPremUser() && isUserManagementEnabled && (
            <div
              className={classNames(styles.menuItem, styles.settingIcon, {
                [styles.active]: isWorkspaceSettingsPage,
              })}
            >
              <VLTooltip title={"Workspace Settings"} placement="right">
                <Link
                  className={`${styles.workspaceSettingIcon} ${isWorkspaceSettingsPage ? styles.active : ""}`}
                  onClick={() => navigate("/workspace-settings")}
                >
                  <SettingsIcon />
                </Link>
              </VLTooltip>
            </div>
          )}

          <div className={styles.docsIcon}>
            <VLTooltip title={"Go to documentation"} placement="right">
              <Link
                href={"https://docs.visual-layer.com"}
                target="_blank"
                onClick={handleDocsButtonClick}
              >
                <FiBookOpen size="30" />
              </Link>
            </VLTooltip>
          </div>
          {isFastdupUser() && (
            <VLTooltip
              placement="right"
              fitContent={true}
              title={
                <div className={styles.localTooltip}>
                  <div>
                    <b>Local visual layer deployment</b>
                    <br />
                    Your Data Never Leaves This Environment
                  </div>
                  <button
                    className={styles.learnMoreButton}
                    onClick={handleLearnMoreButtonClick}
                  >
                    LEARN MORE
                  </button>
                </div>
              }
            >
              <div className={styles.localIcon}>
                <LocalIcon />
              </div>
            </VLTooltip>
          )}
          {showBugReporterIcon && (
            <div
              className={styles.bugReporterIcon}
              onClick={handleBugReporterClick}
            >
              <BugReporter />
            </div>
          )}
          <div className={avatarClass} onClick={handleAvatarClick}>
            <VLAvatar size="medium" src={userAvatar} />
            {!!vlVersion && (
              <div className={styles.vlVersionBanner}>{vlVersion}</div>
            )}
          </div>
        </div>
        <ProfileModal userAvatar={userAvatar} />
        <InvitationModal />
        {showBugReporter && (
          <BugReporterModal
            onClose={() => {
              dispatch(setShowBugReportModal(false));
            }}
            onSubmit={async ({
              title,
              description,
            }: {
              title: string;
              description?: string;
            }) => {
              await dispatch(submitBugReport({ title, description }) as any);
              dispatch(setShowBugReportModal(false));
            }}
          />
        )}
      </div>
    </div>
  );
};

export default Sidebar;
